/**
 * utils/realtime.js — Iter-181 Messaging Slice 3.
 *
 * Single shared WebSocket connection to /api/ws/realtime with:
 *   • exponential backoff reconnect (1s → 30s)
 *   • mobile network recovery (window 'online' + 'visibilitychange')
 *   • client-side ping every 25s (servers usually idle-disconnect at 30s)
 *   • event emitter (subscribe to a channel + listen for events)
 *
 * Falls back to caller-driven polling if the socket can't open.
 */

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const WS_PATH = '/api/ws/realtime';

class RealtimeClient {
  constructor() {
    this.ws = null;
    this.token = null;
    this.connected = false;
    this.connecting = false;
    this.subscribedChannels = new Set();
    this.listeners = new Set(); // (event) => void
    this.backoff = 1000;
    this.maxBackoff = 30000;
    this.pingTimer = null;
    this.reconnectTimer = null;
    this.failed = false; // true after we give up; callers should poll.
    this.lastConnectAt = 0;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this.token) this.connect(this.token, { force: true });
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !this.connected && this.token) {
          this.connect(this.token, { force: true });
        }
      });
    }
  }

  on(handler) {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  _emit(event) {
    this.listeners.forEach((fn) => {
      try { fn(event); } catch (_) { /* noop */ }
    });
  }

  connect(token, { force = false } = {}) {
    this.token = token;
    if (!token) return;
    if (this.connected && !force) return;
    if (this.connecting) return;
    this.connecting = true;
    this.failed = false;

    const httpBase = BASE.replace(/^http/, 'ws');
    const url = `${httpBase}${WS_PATH}?token=${encodeURIComponent(token)}`;

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.connecting = false;
      this.failed = true;
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.connected = true;
      this.connecting = false;
      this.backoff = 1000;
      this.lastConnectAt = Date.now();
      // Re-subscribe to channels we were on.
      for (const cid of this.subscribedChannels) {
        try { ws.send(JSON.stringify({ type: 'subscribe', channel_id: cid })); } catch (_) { /* noop */ }
      }
      this._emit({ type: 'connected' });
      this._startPing();
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        this._emit(msg);
      } catch (_) { /* noop */ }
    };

    ws.onclose = () => {
      this.connected = false;
      this.connecting = false;
      this._stopPing();
      this._emit({ type: 'disconnected' });
      this._scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose will follow.
    };
  }

  _scheduleReconnect() {
    if (!this.token) return;
    if (this.reconnectTimer) return;
    if (this.backoff >= this.maxBackoff) {
      this.failed = true;
      this._emit({ type: 'fallback_polling' });
    }
    const delay = this.backoff;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.token, { force: true });
    }, delay);
    this.backoff = Math.min(this.maxBackoff, this.backoff * 2);
  }

  _startPing() {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === 1) {
        try { this.ws.send(JSON.stringify({ type: 'ping' })); } catch (_) { /* noop */ }
      }
    }, 25000);
  }

  _stopPing() {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  subscribe(channelId) {
    if (!channelId) return;
    this.subscribedChannels.add(channelId);
    if (this.connected && this.ws) {
      try { this.ws.send(JSON.stringify({ type: 'subscribe', channel_id: channelId })); } catch (_) { /* noop */ }
    }
  }

  unsubscribe(channelId) {
    if (!channelId) return;
    this.subscribedChannels.delete(channelId);
    if (this.connected && this.ws) {
      try { this.ws.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId })); } catch (_) { /* noop */ }
    }
  }

  sendTyping(channelId, isTyping) {
    if (this.connected && this.ws) {
      try { this.ws.send(JSON.stringify({ type: 'typing', channel_id: channelId, is_typing: !!isTyping })); } catch (_) { /* noop */ }
    }
  }

  sendRead(channelId) {
    if (this.connected && this.ws) {
      try { this.ws.send(JSON.stringify({ type: 'read', channel_id: channelId })); } catch (_) { /* noop */ }
    }
  }

  disconnect() {
    this.token = null;
    this.subscribedChannels.clear();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this._stopPing();
    if (this.ws) {
      try { this.ws.close(); } catch (_) { /* noop */ }
    }
    this.ws = null;
    this.connected = false;
  }

  isFallback() {
    return this.failed;
  }
}

export const realtime = new RealtimeClient();
